import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Gift, Users, Coffee, Mail, Sparkles, TestTube,
  Shield, Database, Globe, DollarSign, GitBranch, Monitor,
  Send, Calendar, Link, ExternalLink, Copy, Check, Search,
  Code, Cake, Heart, Zap, Settings, BookOpen, Bug
} from 'lucide-react';

interface LinkItem {
  path: string;
  title: string;
  description: string;
  icon: React.ElementType;
  category: 'main' | 'admin' | 'test' | 'special';
  isExternal?: boolean;
}

const LinksDirectory: React.FC = () => {
  const [copied, setCopied] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const links: LinkItem[] = [
    // Main Features
    {
      path: '/',
      title: 'Birthday Card Creator',
      description: 'Main flow to create and send animated birthday cards with coffee gifts. Step-by-step wizard with multiple delivery options.',
      icon: Gift,
      category: 'main'
    },
    {
      path: '/hub',
      title: 'Birthday Hub',
      description: 'Manage all your contacts\' birthdays in one place. Add, edit, and track upcoming birthdays with quick-send options.',
      icon: Users,
      category: 'main'
    },
    {
      path: '/cards',
      title: 'Cards Flow',
      description: 'Alternative entry point to the card creation flow. Same as homepage but accessible via direct URL.',
      icon: Cake,
      category: 'main'
    },
    {
      path: '/landing',
      title: 'Landing Page',
      description: 'Marketing landing page showcasing Birthday Coffee features with animated coffee cups and call-to-action.',
      icon: Coffee,
      category: 'main'
    },
    {
      path: '/test',
      title: 'Testing Flow',
      description: 'Test the birthday card flow with pre-filled data and debug information.',
      icon: TestTube,
      category: 'test'
    },

    // Admin Tools
    {
      path: '/admin',
      title: 'Admin Dashboard',
      description: 'Central hub for all admin tools and system management features.',
      icon: Settings,
      category: 'admin'
    },
    {
      path: '/admin/secrets',
      title: 'Secrets Manager',
      description: 'Manage CloudFlare KV store secrets and environment variables. View, add, edit, and delete secure configuration.',
      icon: Shield,
      category: 'admin'
    },
    {
      path: '/admin/crud-test',
      title: 'Birthday Card CRUD Test',
      description: 'Test CRUD operations for birthday cards. Create, read, update, and delete cards with the KV store.',
      icon: Database,
      category: 'admin'
    },
    {
      path: '/admin/email-test',
      title: 'Email Test Tool',
      description: 'Test email delivery with Resend API. Send test emails and verify configuration.',
      icon: Mail,
      category: 'admin'
    },
    {
      path: '/admin/dns',
      title: 'DNS Checker',
      description: 'Check DNS records for birthday.coffee domain. Verify A, CNAME, MX, and TXT records.',
      icon: Globe,
      category: 'admin'
    },
    {
      path: '/admin/monetization',
      title: 'Monetization Flow',
      description: 'Visual diagram of the monetization strategy and revenue flow for Birthday Coffee.',
      icon: DollarSign,
      category: 'admin'
    },
    {
      path: '/admin/decision-tree',
      title: 'Decision Tree Visualizer',
      description: 'Interactive decision tree for understanding the birthday card creation flow logic.',
      icon: GitBranch,
      category: 'admin'
    },
    {
      path: '/admin/interactive-tree',
      title: 'Interactive Decision Tree',
      description: 'Clickable version of the decision tree with expandable nodes and detailed information.',
      icon: GitBranch,
      category: 'admin'
    },

    // Special Routes & Examples
    {
      path: '/card/coffee-blast/Sarah',
      title: 'Example Shared Card',
      description: 'Example of a shared birthday card link. Shows how recipients see their personalized birthday card.',
      icon: Sparkles,
      category: 'special'
    },
    {
      path: '/cards-old',
      title: 'Legacy Cards Page',
      description: 'Original animated birthday cards page (deprecated but still accessible).',
      icon: Monitor,
      category: 'special'
    },
    {
      path: '/?devMode=true',
      title: 'Dev Mode',
      description: 'Main flow with developer mode enabled. Shows current step and data object for debugging.',
      icon: Bug,
      category: 'special'
    },
    {
      path: '/links',
      title: 'Links Directory (This Page)',
      description: 'Comprehensive directory of all routes and features in Birthday Coffee.',
      icon: BookOpen,
      category: 'special'
    }
  ];

  const categories = {
    main: { title: 'Main Features', icon: Gift, color: 'from-amber-400 to-orange-400' },
    admin: { title: 'Admin Tools', icon: Shield, color: 'from-purple-400 to-indigo-400' },
    test: { title: 'Testing', icon: TestTube, color: 'from-green-400 to-emerald-400' },
    special: { title: 'Special Routes', icon: Sparkles, color: 'from-pink-400 to-rose-400' }
  };

  const filteredLinks = links.filter(link =>
    link.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    link.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    link.path.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(`https://birthday.coffee${text}`);
    setCopied(text);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-4 mb-4">
            <a
              href="/"
              className="p-2 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow"
            >
              <ArrowLeft className="w-5 h-5" />
            </a>
            <div>
              <h1 className="text-4xl font-bold text-gray-800">Birthday Coffee Links Directory</h1>
              <p className="text-gray-600 mt-2">All the awesome features we've built, in one place! 🎉</p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative mt-6">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search links, features, or descriptions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            {Object.entries(categories).map(([key, cat]) => {
              const count = filteredLinks.filter(l => l.category === key).length;
              const Icon = cat.icon;
              return (
                <div key={key} className="bg-white rounded-xl p-4 shadow-md">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-gradient-to-br ${cat.color}`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{count}</p>
                      <p className="text-sm text-gray-600">{cat.title}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Links by Category */}
        {Object.entries(categories).map(([categoryKey, categoryInfo]) => {
          const categoryLinks = filteredLinks.filter(link => link.category === categoryKey);
          if (categoryLinks.length === 0) return null;

          const CategoryIcon = categoryInfo.icon;

          return (
            <motion.div
              key={categoryKey}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-8"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-lg bg-gradient-to-br ${categoryInfo.color}`}>
                  <CategoryIcon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800">{categoryInfo.title}</h2>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {categoryLinks.map((link) => {
                  const Icon = link.icon;
                  const isActive = copied === link.path;

                  return (
                    <motion.div
                      key={link.path}
                      whileHover={{ scale: 1.02 }}
                      className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all p-6"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg bg-gradient-to-br ${categoryInfo.color}`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <h3 className="font-semibold text-lg text-gray-800">{link.title}</h3>
                        </div>
                        <button
                          onClick={() => copyToClipboard(link.path)}
                          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Copy link"
                        >
                          {isActive ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </div>

                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {link.description}
                      </p>

                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded flex-1 truncate">
                          {link.path}
                        </code>
                        <a
                          href={link.path}
                          className="p-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          );
        })}

        {/* Footer Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 p-6 bg-white rounded-xl shadow-md"
        >
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Code className="w-5 h-5" />
            Quick Development Tips
          </h3>
          <div className="space-y-2 text-sm text-gray-600">
            <p>• <strong>Component Inspector:</strong> Press Ctrl+Shift+I to toggle the UCI (Universal Component Inspector)</p>
            <p>• <strong>Dev Mode:</strong> Add ?devMode=true to any URL to see debug information</p>
            <p>• <strong>Local Dev:</strong> Run <code className="bg-gray-100 px-1 rounded">npm run dev</code> from apps/web directory</p>
            <p>• <strong>Deploy:</strong> <code className="bg-gray-100 px-1 rounded">npm run build && npx wrangler pages deploy dist --project-name=birthday-coffee</code></p>
            <p>• <strong>Domain:</strong> Production at https://birthday.coffee</p>
            <p>• <strong>API:</strong> Workers at https://birthday-coffee-api.b-9f2.workers.dev</p>
          </div>
        </motion.div>

        {/* Features Summary */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-6 p-6 bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl"
        >
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Key Features Built
          </h3>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <div>✅ Step-by-step birthday card creation</div>
            <div>✅ Email, SMS, WhatsApp, and link sharing</div>
            <div>✅ Coffee gift integration ($5-$50)</div>
            <div>✅ Birthday Hub for contact management</div>
            <div>✅ Animated card previews</div>
            <div>✅ CloudFlare KV storage</div>
            <div>✅ Resend email integration</div>
            <div>✅ Custom domain routing</div>
            <div>✅ Admin dashboard with tools</div>
            <div>✅ Universal Component Inspector (UCI)</div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LinksDirectory;